import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import PropTypes from 'prop-types';
import {Collapse} from 'react-collapse';
import {Alert, Button, Form, Input, Select} from 'antd';
import {StyledEditBudgetModal} from '../../BudgetActiveDetailsPage/EditBudgetModal/StyledEditBudgetModal';
import SpinSmall from '../../../SpinSmall';
import {formHelpers} from '../../../../utils/helpers';
import {useFormHelpers} from '../../../../hooks';

const {Item} = Form;

const initialFormValues = {
  approver: undefined,
  step_name: '',
};

const AddApprovalStepModal = ({
  handleOk,
  handleCancel,
  loading = false,
  mode = 'add',
  open,
  step,
  ...rest
}) => {
  const [t] = useTranslation(['main', 'budgets']);
  const [form] = Form.useForm();
  const [error, setError] = useState(null);

  const {employeeOptions, placeholderEnter, placeholderSelect} = useFormHelpers();

  useEffect(() => {
    if (open && mode === 'edit' && step) {
      form.setFieldsValue({
        approver: step?.approver?.email || undefined,
        step_name: step?.step_name || '',
      });
    }
  }, [form, mode, open, step]);

  const handleAfterOpenChange = (open) => {
    if (!open) {
      form.setFieldsValue(initialFormValues);
      if (error) setError(null);
    }
  }

  const handleSubmit = (fields) => {
    let data = {};
    if (mode === 'add') {
      data = { ...fields };
    } else {
      if (fields.approver !== step?.approver?.email) {
        data = { ...data, new_approver: fields.approver };
      }
      data = { ...data, step_name: fields.step_name };
    }

    handleOk?.({
      data,
      errorCallback: (response) => formHelpers.handleFormErrors({form, t, setError, response})
    });
  }

  const rules = [{required: true, message: t('validation.fieldIsRequired')}];

  const actionTitle = t(mode === 'add' ? 'create' : 'edit');

  const footer = [
    <Button
      key='next'
      disabled={loading}
      onClick={() => form.submit()}
      size='large'
      type='primary'
    >
      {actionTitle}
    </Button>
  ];

  return (
    <StyledEditBudgetModal
      afterOpenChange={handleAfterOpenChange}
      footer={footer}
      forceRender
      onClose={handleCancel}
      open={open}
      title={`${actionTitle} ${t('Budget')}`}
      width={480}
      {...rest}
    >
      <SpinSmall spinning={loading}>
        <Form
          initialValues={initialFormValues}
          form={form}
          layout='vertical'
          onFinish={handleSubmit}
          requiredMark={false}
        >
          <Item
            label={`${t('step')} ${t('name')}`}
            name='step_name'
            required
            rules={rules}
          >
            <Input placeholder={placeholderEnter('name')} size='large' />
          </Item>
          <Item
            label={`${t('step')} ${t('Owner')}`}
            name='approver'
            required
            rules={rules}
          >
            <Select
              optionFilterProp='optionLabel'
              options={employeeOptions}
              placeholder={placeholderSelect('user')}
              size='large'
              showSearch
            />
          </Item>
        </Form>
        <Collapse isOpened={Boolean(error)}>
          <Alert message={error} type='error' showIcon />
        </Collapse>
      </SpinSmall>
    </StyledEditBudgetModal>
  );
}

AddApprovalStepModal.propTypes = {
  handleCancel: PropTypes.func,
  handleOk: PropTypes.func,
  loading: PropTypes.bool,
  mode: PropTypes.oneOf(['add', 'edit']),
  open: PropTypes.bool.isRequired,
  step: PropTypes.object,
}

export default AddApprovalStepModal;
